// Admin bulk product import page. Paste a CSV (EPA PPIS export, vendor
// catalog, whatever) → preview → commit. No live API scraping; the CSV is
// the single import path because it works for every data source and is
// re-runnable offline.

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { UserRole } from '@greeniq/shared';
import { ApiError } from '../api/client.js';
import { importProductsCsv, type BulkImportResponse } from '../api/chemical.js';
import { useMe } from '../context/MeContext.js';

const EXAMPLE_CSV = `epa_reg_no,name,manufacturer,category,active_ingredients,frac_code,irac_code,hrac_code,rei_hours,phi_days,signal_word,typical_rate_per_1000sqft
50534-188,Daconil Weather Stik,Syngenta,fungicide,chlorothalonil|54%,M5,,,12,,WARNING,3.25 fl oz
100-1470,Velista,Syngenta,fungicide,penthiopyrad|20%,7,,,12,,CAUTION,0.5 oz`;

export function ProductImportPage() {
  const { courseSlug } = useParams();
  const { me } = useMe();
  const role: UserRole | undefined = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;
  const isAdmin = role === 'ageye_admin';

  const [csv, setCsv] = useState('');
  const [response, setResponse] = useState<BulkImportResponse | null>(null);
  const [loading, setLoading] = useState<'preview' | 'commit' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function runPreview() {
    if (!courseSlug) return;
    setLoading('preview');
    setError(null);
    try {
      const r = await importProductsCsv(courseSlug, csv, 'preview');
      setResponse(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(null);
    }
  }

  async function runCommit() {
    if (!courseSlug) return;
    if (!confirm('Commit the import? This will upsert products in the catalog.')) return;
    setLoading('commit');
    setError(null);
    try {
      const r = await importProductsCsv(courseSlug, csv, 'commit');
      setResponse(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(null);
    }
  }

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-3xl">
        <Link to={`/app/${courseSlug}/chemical`} className="text-sm text-greeniq-400 hover:underline">
          ← Chemical &amp; Inventory
        </Link>
        <p className="mt-4 text-fg-muted">AGEYE admin only.</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <Link to={`/app/${courseSlug}/chemical`} className="text-sm text-greeniq-400 hover:underline">
        ← Chemical &amp; Inventory
      </Link>

      <div className="mt-3">
        <p className="uppercase tracking-wider text-xs text-greeniq-400 font-semibold">Admin</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-display font-bold text-fg">
          Bulk product import
        </h1>
        <p className="mt-1 text-fg-muted">
          Paste a CSV export (EPA PPIS, vendor catalog, etc.) — preview first, then commit. Upserts by <code className="font-mono">epa_reg_no</code> or by name+manufacturer for products without EPA registration.
        </p>
      </div>

      <div className="mt-8">
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-fg-muted">
            CSV content
          </span>
          <textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            rows={14}
            spellCheck={false}
            className="mt-1 w-full border border-border rounded-md py-2 px-2 font-mono text-xs"
            placeholder={EXAMPLE_CSV}
          />
        </label>
        <details className="mt-2 text-xs text-fg-muted">
          <summary className="cursor-pointer hover:text-fg">Expected columns</summary>
          <pre className="mt-2 p-2 bg-bg-elevated rounded-md overflow-auto">{`epa_reg_no,name,manufacturer,category,active_ingredients,frac_code,irac_code,hrac_code,rei_hours,phi_days,signal_word,typical_rate_per_1000sqft

- name is required, all others optional
- category: fungicide | herbicide | insecticide | fertilizer | pgr | wetting_agent | adjuvant | other
- active_ingredients: pipe-separated as "name|amount;name|amount" OR JSON array [{"name":"x","amount":"y"}]`}</pre>
        </details>
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => void runPreview()}
          disabled={csv.length === 0 || loading !== null}
          className="px-4 py-2 rounded-md bg-bg-hover text-greeniq-300 text-sm font-semibold hover:bg-bg-elevated disabled:opacity-50"
        >
          {loading === 'preview' ? 'Previewing…' : 'Preview'}
        </button>
        <button
          onClick={() => void runCommit()}
          disabled={csv.length === 0 || loading !== null}
          className="px-4 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-sm font-semibold hover:bg-greeniq-400 disabled:opacity-50"
        >
          {loading === 'commit' ? 'Committing…' : 'Commit import'}
        </button>
        <button
          onClick={() => setCsv(EXAMPLE_CSV)}
          className="px-4 py-2 rounded-md text-sm text-fg-secondary hover:text-fg"
        >
          Load example
        </button>
      </div>

      {error && <div className="mt-4 p-3 rounded-md bg-error-muted border border-[color:var(--color-error)]/30 text-sm text-[color:var(--color-error)]">{error}</div>}

      {response && (
        <section className="mt-8">
          <div className="flex items-center gap-3 flex-wrap">
            <Chip label="Total rows" value={response.totalRows} />
            <Chip label="Insert" value={response.inserted} color="green" />
            <Chip label="Update" value={response.updated} color="amber" />
            <Chip label="Errors" value={response.errors} color="red" />
            <span className="text-xs text-fg-muted">
              Mode: <strong>{response.mode}</strong>
            </span>
          </div>

          {response.headerWarnings.length > 0 && (
            <div className="mt-3 p-3 bg-warning-muted border border-[color:var(--color-warning)]/30 rounded-md text-sm text-[color:var(--color-warning)]">
              <strong>Header warnings:</strong> {response.headerWarnings.join(', ')}
            </div>
          )}

          <div className="mt-4 max-h-[400px] overflow-auto bg-bg-card rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-border bg-bg-elevated">
                  <th className="px-3 py-2 font-semibold text-fg-muted">Row</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted">EPA</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted">Name</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted">Action</th>
                  <th className="px-3 py-2 font-semibold text-fg-muted">Reason</th>
                </tr>
              </thead>
              <tbody>
                {response.previews.map((p) => (
                  <tr key={p.rowNumber} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 text-fg-muted">{p.rowNumber}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.epaRegNo ?? '—'}</td>
                    <td className="px-3 py-2">{p.name}</td>
                    <td className="px-3 py-2">
                      <span className={actionBadgeClass(p.action)}>{p.action}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-fg-muted">{p.reason ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function Chip({ label, value, color }: { label: string; value: number; color?: 'green' | 'amber' | 'red' }) {
  const cls =
    color === 'green' ? 'bg-success-muted text-[color:var(--color-success)]'
    : color === 'amber' ? 'bg-warning-muted text-[color:var(--color-warning)]'
    : color === 'red' ? 'bg-error-muted text-[color:var(--color-error)]'
    : 'bg-bg-hover text-greeniq-300';
  return (
    <span className={`${cls} px-3 py-1.5 rounded-full text-xs font-semibold`}>
      {label}: {value}
    </span>
  );
}

function actionBadgeClass(action: string): string {
  const base = 'px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider';
  if (action === 'insert') return `${base} bg-success-muted text-[color:var(--color-success)]`;
  if (action === 'update') return `${base} bg-warning-muted text-[color:var(--color-warning)]`;
  if (action === 'error') return `${base} bg-error-muted text-[color:var(--color-error)]`;
  return `${base} bg-bg-elevated text-fg-secondary`;
}
